import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SelectedCompanyContext } from '../contexts/SelectedCompanyContext';
import api from '../api';
import { getFallbackTemplates, useCompanyTaskTemplates } from '../hooks/queries/useTaskTemplates';
import { 
  AnimatedBackground,
  ModernCard,
  ModernStatCard,
  ModernProgressRing,
  ModernBadge,
  getStatusVariant
} from './ui/modern';

const OnboardingOverview = () => {
  const { selected } = useContext(SelectedCompanyContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [preview, setPreview] = useState(null);
  const { data: templatesData } = useCompanyTaskTemplates(selected?.id);

  useEffect(() => {
    const load = async () => {
      if (!selected) return;
      try {
        const res = await api.get(`/tasks/company/${selected.id}`);
        setTasks(res.data);
      } catch (e) {
        console.error('load tasks overview', e);
        setTasks(selected.CompanyTasks || []);
      }

      try {
        const res = await api.get(`/reports/company/${selected.id}/preview`);
        setPreview(res.data || null);
      } catch (e) {
        console.error('load preview progress', e);
        setPreview(null);
      }

      setLoading(false);
    };
    load();
  }, [selected]);

  if (!selected || loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <div className="text-gray-900 dark:text-white text-lg">Loading onboarding overview...</div>
      </div>
    );
  }

  const progressPercent = typeof preview?.progress?.percentage === 'number' ? preview.progress.percentage : 0;
  const allTasks = typeof preview?.progress?.total === 'number' ? preview.progress.total : 0;
  const completedTasks = typeof preview?.progress?.completed === 'number' ? preview.progress.completed : 0;

  const branchStats = (preview?.branches || []).map(b => ({
    id: b.id,
    name: b.name,
    total: b.total,
    done: b.completed,
    percent: b.percentage,
  }));

  const templates = (Array.isArray(templatesData) && templatesData.length > 0)
    ? templatesData
    : getFallbackTemplates();

  const internalTemplates = templates
    .filter((t) => t.group === 'internalSystems' && t.isActive !== false)
    .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));

  const companyWideTemplates = templates
    .filter((t) => t.group === 'companyWide' && t.isActive !== false)
    .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));

  const statusPriority = ['Completed', 'Complete', 'In Progress', 'On Hold', 'Not Started'];
  const pickBestStatus = (items) => {
    if (!items.length) return 'Not Started';
    for (const st of statusPriority) {
      if (items.some(t => t.status === st)) return st === 'Complete' ? 'Completed' : st;
    }
    return 'Not Started';
  };

  // IMPORTANT: Only show tasks that were actually selected/created for this company.
  // Previously we mapped fixed lists and defaulted missing tasks to "Not Started", which made unselected tasks
  // appear in the UI and skewed % calculations.
  const internalStats = internalTemplates
    .map((tpl) => {
      const list = tasks.filter((t) => t.type === tpl.title && !t.branchId);
      if (!list.length) return null;
      const bestStatus = pickBestStatus(list);
      const first = list[0];
      return { name: tpl.title, status: bestStatus, statusNotes: first?.CompanyTaskNotes || [], taskId: first?.id };
    })
    .filter(Boolean);

  const companyWideStats = companyWideTemplates
    .map((tpl) => {
      const list = tasks.filter((t) => t.type === tpl.title && !t.branchId);
      if (!list.length) return null;
      const bestStatus = pickBestStatus(list);
      const first = list[0];
      return { name: tpl.title, status: bestStatus, statusNotes: first?.CompanyTaskNotes || [], taskId: first?.id };
    })
    .filter(Boolean);

  // Navigation handlers
  const handleBranchClick = (branchId) => {
    navigate(`/onboarding/${selected.id}/branches/${branchId}`);
  };

  const handleTaskClick = (taskType) => {
    navigate(`/onboarding/${selected.id}/tasks`, { state: { highlightTask: taskType } });
  };

  const pctList = (arr) => {
    const total = arr.length;
    const done = arr.filter(t => t.status === 'Completed' || t.status === 'Complete').length;
    return total ? Math.round((done / total) * 100) : 0;
  };

  const internalPct = pctList(internalStats);
  const companyPct = pctList(companyWideStats);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <AnimatedBackground />

      <div className="relative z-10 p-6 lg:p-8 max-w-7xl mx-auto">
        {/* Hero Section */}
        <ModernCard className="mb-8 text-center">
          <div className="flex flex-col items-center">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{selected.name}</h1>
            <p className="text-gray-600 dark:text-slate-400 mb-8">Onboarding Overview</p>
            <ModernProgressRing percentage={progressPercent} size={200} strokeWidth={14} />
          </div>
        </ModernCard>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <ModernStatCard
            title="Total Items"
            value={allTasks}
            icon="📋"
            color="blue"
          />
          <ModernStatCard
            title="Completed"
            value={completedTasks}
            icon="✓"
            color="emerald"
          />
          <ModernStatCard
            title="Branches"
            value={selected.Branches?.length || 0}
            icon="🏢"
            color="violet"
          />
          <ModernStatCard
            title="Progress"
            value={`${progressPercent}%`}
            icon="📊"
            color="amber"
          />
        </div>

        {/* Three Column Layout */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Internal Systems */}
          <ModernCard>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span>🔧</span>
              Internal Systems
            </h3>
            <div className="flex items-center justify-center mb-6">
              <ModernProgressRing percentage={internalPct} size={120} strokeWidth={10} label="Complete" />
            </div>
            <div className="space-y-3">
              {internalStats.map(task => (
                <div 
                  key={task.name} 
                  onClick={() => handleTaskClick(task.name)}
                  className="flex justify-between items-center p-3 bg-gray-100 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/5 cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 hover:border-blue-500/30 transition-all group"
                >
                  <span className="text-gray-700 dark:text-slate-300 text-sm group-hover:text-gray-900 dark:group-hover:text-white transition-colors">{task.name}</span>
                  <div className="flex items-center gap-2">
                    <ModernBadge variant={getStatusVariant(task.status)} size="sm">
                      {task.status}
                    </ModernBadge>
                    <svg className="w-4 h-4 text-gray-500 dark:text-slate-500 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          </ModernCard>

          {/* Company-wide Tasks */}
          <ModernCard>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span>🌐</span>
              Company-wide
            </h3>
            <div className="flex items-center justify-center mb-6">
              <ModernProgressRing percentage={companyPct} size={120} strokeWidth={10} label="Complete" />
            </div>
            <div className="space-y-3">
              {companyWideStats.length === 0 ? (
                <div className="text-center py-6 text-gray-600 dark:text-slate-400 text-sm">
                  No company-wide tasks selected.
                </div>
              ) : companyWideStats.map(task => (
                <div 
                  key={task.name} 
                  onClick={() => handleTaskClick(task.name)}
                  className="flex justify-between items-center p-3 bg-gray-100 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/5 cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 hover:border-blue-500/30 transition-all group"
                >
                  <span className="text-gray-700 dark:text-slate-300 text-sm group-hover:text-gray-900 dark:group-hover:text-white transition-colors">{task.name}</span>
                  <div className="flex items-center gap-2">
                    <ModernBadge variant={getStatusVariant(task.status)} size="sm">
                      {task.status}
                    </ModernBadge>
                    <svg className="w-4 h-4 text-gray-500 dark:text-slate-500 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          </ModernCard>

          {/* Branches */}
          <ModernCard>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span>🏢</span>
              Branches
            </h3>
            <div className="space-y-4">
              {branchStats.map(bs => (
                <div 
                  key={bs.id} 
                  onClick={() => handleBranchClick(bs.id)}
                  className="bg-gray-100 dark:bg-white/5 rounded-lg p-4 border border-gray-200 dark:border-white/5 cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 hover:border-blue-500/30 transition-all group"
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-900 dark:text-white font-medium group-hover:text-blue-400 transition-colors">{bs.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600 dark:text-slate-400 text-sm">{bs.percent}%</span>
                      <svg className="w-4 h-4 text-gray-500 dark:text-slate-500 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-emerald-500 h-full transition-all duration-500"
                      style={{ width: `${bs.percent}%` }}
                    />
                  </div>
                  <div className="flex justify-between items-center mt-2 text-xs text-gray-500 dark:text-slate-500">
                    <span>{bs.done} completed</span>
                    <span>{bs.total} total</span>
                  </div>
                </div>
              ))}
              {branchStats.length === 0 && (
                <div className="text-center text-gray-500 dark:text-slate-500 py-8">
                  No branches added yet
                </div>
              )}
            </div>
          </ModernCard>
        </div>

        {/* Branch Progress Table */}
        {selected.Branches?.length > 0 && (
          <ModernCard className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span>📊</span>
              Branch Progress Details
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-white/10">
                    <th className="text-left p-3 text-gray-600 dark:text-slate-400 font-medium">Branch</th>
                    <th className="text-center p-3 text-gray-600 dark:text-slate-400 font-medium">Total Tasks</th>
                    <th className="text-center p-3 text-gray-600 dark:text-slate-400 font-medium">Completed</th>
                    <th className="text-center p-3 text-gray-600 dark:text-slate-400 font-medium">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {branchStats.map(bs => (
                    <tr 
                      key={bs.id} 
                      onClick={() => handleBranchClick(bs.id)}
                      className="border-b border-gray-200 dark:border-white/5 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors cursor-pointer group"
                    >
                      <td className="p-3 text-gray-900 dark:text-white group-hover:text-blue-400 transition-colors">
                        <div className="flex items-center gap-2">
                          {bs.name}
                          <svg className="w-4 h-4 text-gray-500 dark:text-slate-500 group-hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </div>
                      </td>
                      <td className="p-3 text-center text-gray-700 dark:text-slate-300">{bs.total}</td>
                      <td className="p-3 text-center text-emerald-600 dark:text-emerald-400">{bs.done}</td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-24 bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-blue-500 to-emerald-500 h-full rounded-full"
                              style={{ width: `${bs.percent}%` }}
                            />
                          </div>
                          <span className="text-gray-700 dark:text-slate-300 text-sm font-medium">{bs.percent}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ModernCard>
        )}

        {/* Task Details Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Internal System Setup */}
          <ModernCard>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span>⚙️</span>
              Internal System Setup
            </h2>
            <div className="space-y-2">
              {internalStats.map(row => (
                <div 
                  key={row.name} 
                  onClick={() => handleTaskClick(row.name)}
                  className="p-3 bg-gray-100 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/5 hover:bg-gray-200 dark:hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-pointer group"
                >
                  <div className="flex justify-between items-center">
                    <span className="text-gray-900 dark:text-white font-medium group-hover:text-blue-400 transition-colors">{row.name}</span>
                    <div className="flex items-center gap-2">
                      <ModernBadge variant={getStatusVariant(row.status)}>
                        {row.status}
                      </ModernBadge>
                      <svg className="w-4 h-4 text-gray-500 dark:text-slate-500 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ModernCard>

          {/* Company-wide Tasks Details */}
          <ModernCard>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span>🎯</span>
              Company-wide Tasks
            </h2>
            <div className="space-y-3">
              {companyWideStats.map(row => (
                <div key={row.name}>
                  <div 
                    onClick={() => handleTaskClick(row.name)}
                    className="p-3 bg-gray-100 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/5 hover:bg-gray-200 dark:hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-pointer group"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-900 dark:text-white font-medium group-hover:text-blue-400 transition-colors">{row.name}</span>
                      <div className="flex items-center gap-2">
                        <ModernBadge variant={getStatusVariant(row.status)}>
                          {row.status}
                        </ModernBadge>
                        <svg className="w-4 h-4 text-gray-500 dark:text-slate-500 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                    {/* Notes */}
                    {row.statusNotes?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {row.statusNotes.map((n, i) => (
                          <div key={i} className="text-xs text-gray-600 dark:text-slate-400 pl-3 border-l-2 border-blue-500/30">
                            • {n.note} <span className="text-gray-500 dark:text-slate-500">— {n.author?.name || ''} {new Date(n.createdAt).toLocaleDateString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ModernCard>
        </div>
      </div>
    </div>
  );
};

export default OnboardingOverview;
